import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { capitalize } from "lodash";
import moment from "moment";
import { FC, useCallback, useEffect, useState } from "react";
import { ActionIcon, Tooltip } from "rizzui";
import { toast } from "sonner";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import useHandleErrors from "../../../hooks/useHandleErrors";
import CustomTable from "../../../components/table/CustomTable";
import useFetch from "../../../hooks/useFetch";
import Page from "../../../layout/Page/Page";
import { MdOutlineModeEditOutline, MdDeleteOutline } from "react-icons/md";
import { FormDrawerView } from "../../../components/forms/FormDrawer"; 
import GeneralExpenseFilters from "./GeneralExpenseFilters";
import GeneralExpenseForm from "../components/GeneralExpenseForm";
import { GeneralExpenseService } from "../../../services/generalExpense/generalExpenseService";
import DeletePopover from "../../../components/buttons/DeletePopover";

interface GeneralExpenseListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    expenseSelected: any | null;
    setExpenseSelected: React.Dispatch<React.SetStateAction<any | null>>;
    registerTableRefetch?: (refetch: () => void) => void;
}

const columnOrder = [
    'id',
    'concept',
    'date',
    'amount',
    'createdAt',
    'updatedAt'
];

const GeneralExpenseList: FC<GeneralExpenseListProps> = ({ openFormDrawer, setOpenFormDrawer, expenseSelected, setExpenseSelected, registerTableRefetch }) => {

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const service = new GeneralExpenseService();
    const { handleErrors } = useHandleErrors();

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listGeneralExpenses(filters);
        return response.getResponseData();
    }, [filters]));
    useEffect(() => {
        registerTableRefetch?.(refetch);
    }, [registerTableRefetch, refetch]);

    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteGeneralExpense(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Gasto eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    const orderedData = () => data?.data?.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'general-expenses-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={[]}
                    columnsNotShown={['id']}
                    overrideColumns={[
                        {
                            key: 'concept',
                            columnNameKey: 'Concepto',
                            render: (row: any) => row.row.original?.concept || 'N/A',
                        },
                        {
                            key: 'date',
                            columnNameKey: 'Fecha',
                            render: (row: any) => {
                                const date = row.row.original?.date?.date;
                                return date ? moment(date).format('DD/MM/YYYY') : 'N/A';
                            },
                        },
                        {
                            key: 'amount',
                            columnNameKey: 'Importe',
                            render: (row: any) => {
                                return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(row.row.original?.amount);
                            },
                        },
                        {
                            key: 'createdAt',
                            columnNameKey: 'Creación',
                            render: (row: any) => {
                                const date = row.row.original?.createdAt?.date;
                                return date ? moment(date).format('DD/MM/YYYY HH:mm:ss') : 'N/A';
                            },
                        },
                        {
                            key: 'updatedAt',
                            columnNameKey: 'Actualización',
                            render: (row: any) => {
                                const date = row.row.original?.updatedAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es }))}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'Nunca';
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'general_expense', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setExpenseSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white h-6 w-6"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'general_expense', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar gasto`}
                                                description={`¿Estás seguro de que deseas eliminar el gasto ${row.concept}?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    toggleFilters={() => setOpenFilters(true)}
                    pagination={true}
                    paginationData={{
                        totalCount: data?.totalRegisters || 0,
                        currentPage: filters.page,
                        pageCount: data?.lastPage || 1,
                        pageSize: filters.limit,
                        handlePagination: (page: number) => updatePage(page),
                        handlePerPage: (pageSize: number) => updatePageSize(pageSize),
                    }}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle="Filtros gastos generales" onClear={() => resetFilters()}>
                <GeneralExpenseFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <FormDrawerView 
                isOpen={openFormDrawer} 
                setOpenDrawer={setOpenFormDrawer} 
                drawerTitle={expenseSelected ? "Editar Gasto" : "Crear Gasto"}
                submitText={expenseSelected ? "Guardar Gasto" : "Crear Gasto"}
            >
                <GeneralExpenseForm 
                    expense={expenseSelected} 
                    onSuccess={() => {
                        setOpenFormDrawer(false);
                        refetch();
                    }} 
                />
            </FormDrawerView>
        </Page>
    );
};

export default GeneralExpenseList;
